import React, { useState, useEffect, useCallback } from 'react';
import Header from './components/Header';
import StatsGrid from './components/StatsGrid';
import PurchasesGrid from './components/PurchasesGrid';
import ContainersInTransit from './components/ContainersInTransit';
import InventorySection from './components/InventorySection';
import LiveActivity from './components/LiveActivity';
import SwitchTimer from './components/SwitchTimer';
import {
  rwandaSnData,
  rwandaTaData
} from './data/dashboardData';
import {
  fetchExportMetrics,
  fetchPurchaseMetrics,
  fetchContainerSummary,
  fetchShippingStatus
} from './services/apiService';

function App() {
  // Screen sequence: Rwanda-Sn -> Rwanda-Ta (2 screens)
  const screens = ['RWANDA-Sn', 'RWANDA-Ta'];
  const screenData = {
    'RWANDA-Sn': rwandaSnData,
    'RWANDA-Ta': rwandaTaData
  };

  const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(180);
  const [loading, setLoading] = useState(true);

  const currentScreen = screens[currentScreenIndex];
  const currentData = screenData[currentScreen];

  // Export metrics state
  const [exportMetrics, setExportMetrics] = useState({
    totalExport: { weight: 0, value: 0 },
    thisMonthExport: { weight: 0, value: 0 },
    ytdExport: { weight: 0, value: 0 }
  });

  // Purchase metrics state
  const [purchaseMetrics, setPurchaseMetrics] = useState({
    thisWeekPurchases: { weight: 0, value: 0 },
    thisMonthPurchases: { weight: 0, value: 0 },
    ytdPurchases: { weight: 0, value: 0 }
  });

  // Container/Inventory state
  const [containerData, setContainerData] = useState(null);

  // Shipping status state
  const [shippingStatus, setShippingStatus] = useState({
    transit: 0,
    delivered: 0,
    delay: 0,
    total_weight: 0
  });

  // Get minerals for purchases, inventory, and current stock
  const getPurchaseInventoryMinerals = useCallback(() => {
    if (currentScreen.includes('Sn')) {
      return ['Cassiterite']; // Sn screens use Cassiterite
    } else {
      // Ta screens use Coltan
      return ['Coltan'];
    }
  }, [currentScreen]);

  // Get minerals for exports and containers in transit - specific to each screen
  const getExportMinerals = useCallback(() => {
    if (currentScreen === 'DRC-Sn') {
      return ['Tin']; // DRC Sn screen
    } else if (currentScreen === 'DRC-Ta') {
      return ['Tantalum']; // DRC Ta screen
    } else if (currentScreen === 'RWANDA-Sn') {
      return ['Tin']; // Rwanda Sn screen
    } else {
      // RWANDA-Ta
      return ['Tantalum']; // Rwanda Ta screen
    }
  }, [currentScreen]);

  // Get country code for API calls
  const getCountryCode = useCallback(() => {
    if (currentScreen.includes('DRC')) {
      return 'DRC';
    } else {
      return '.Rwanda'; // Use ".Rwanda" with dot prefix
    }
  }, [currentScreen]);

  // Fetch all data from API
  const fetchAllData = useCallback(async () => {
    setLoading(true);

    try {
      const country = getCountryCode();
      const purchaseInventoryMinerals = getPurchaseInventoryMinerals();
      const exportMinerals = getExportMinerals();

      // Fetch all data in parallel with correct minerals for each endpoint
      const [exports, purchases, shipping, containers] = await Promise.all([
        fetchExportMetrics(country, exportMinerals),
        fetchPurchaseMetrics(country, purchaseInventoryMinerals),
        fetchShippingStatus(country, exportMinerals),
        fetchContainerSummary(country, purchaseInventoryMinerals)
      ]);

      // Update export metrics
      setExportMetrics({
        totalExport: exports.totalExport || { weight: 0, value: 0 },
        thisMonthExport: exports.thisMonthExport || { weight: 0, value: 0 },
        ytdExport: exports.ytdExport || { weight: 0, value: 0 }
      });

      // Update purchase metrics
      setPurchaseMetrics({
        thisWeekPurchases: purchases.thisWeekPurchases || { weight: 0, value: 0 },
        thisMonthPurchases: purchases.thisMonthPurchases || { weight: 0, value: 0 },
        ytdPurchases: purchases.ytdPurchases || { weight: 0, value: 0 }
      });

      // Update shipping status
      setShippingStatus({
        transit: shipping.transit || 0,
        delivered: shipping.delivered || 0,
        delay: shipping.delay || 0,
        total_weight: shipping.total_weight || 0
      });

      // Update container data
      setContainerData(containers);

      console.log('All data fetched successfully:', {
        country,
        exportMinerals,
        purchaseInventoryMinerals,
        exports,
        purchases,
        shipping,
        containers
      });

    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [getCountryCode, getPurchaseInventoryMinerals, getExportMinerals]);

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Switch screens when timer reaches 0
  useEffect(() => {
    if (timeRemaining <= 0) {
      setCurrentScreenIndex(current => (current + 1) % screens.length);
      setTimeRemaining(180);
    }
  }, [timeRemaining, screens.length]);

  // Fetch data when screen changes
  useEffect(() => {
    fetchAllData();
  }, [currentScreenIndex, fetchAllData]);

  return (
    <div className="h-screen w-screen bg-[#1a1d29] text-white flex flex-col overflow-hidden" key={currentScreen}>
      <div className="px-6 pt-4 pb-2">
        <Header currentScreen={currentScreen} />
      </div>
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="text-xl text-gray-400">Loading data...</div>
          </div>
        )}

        {!loading && (
          <>
            <StatsGrid
              data={currentData}
              exportMetrics={exportMetrics}
              containerData={containerData}
              key={`stats-${currentScreen}`}
            />
            <PurchasesGrid
              data={currentData}
              purchaseMetrics={purchaseMetrics}
              key={`purchases-${currentScreen}`}
            />
            <div className="grid grid-cols-2 gap-6">
              <ContainersInTransit
                data={currentData}
                shippingStatus={shippingStatus}
                key={`containers-${currentScreen}`}
              />
              <InventorySection
                data={currentData}
                currentScreen={currentScreen}
                containerData={containerData}
                key={`inventory-${currentScreen}`}
              />
            </div>

            <LiveActivity data={currentData} country={getCountryCode()} key={`activity-${currentScreen}`} />
          </>
        )}
      </div>
      <SwitchTimer timeRemaining={timeRemaining} />
    </div>
  );
}

export default App;